import { Alert, AlertDescription } from "@bcc/components/ui/alert";
import { Button } from "@bcc/components/ui/button";
import { Input } from "@bcc/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { CATALOGUE_REGION_IDS, type CatalogueRegionId, isCatalogueRegionId } from "@shared/types/admin-catalogue";
import type { Product } from "@shared/types/product";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { buildCatalogueFilterGroups } from "../admin/admin-catalogue-filter-groups";
import { AdminCatalogueMetrics } from "../admin/admin-catalogue-metrics";
import { adminCatalogueGlobalFilterFn, collectBadgeOptions, computeCatalogueCoverage } from "../admin/admin-catalogue-rows";
import { AdminProductEditProvider } from "../admin/admin-product-edit-context";
import { getAdminProductsColumns } from "../admin/admin-products-columns";
import { DataTable } from "../data-table/data-table";
import { ADMIN_PRODUCTS_PAGE_SIZE_OPTIONS, ADMIN_PRODUCTS_PERSISTENCE_KEY, setAdminProductsViewTestId } from "./admin-products-view.utils";
import { useAdminProductsViewState } from "./use-admin-products-view-state";

interface AdminProductsViewProps {
  readonly products: Product[];
  readonly region: CatalogueRegionId;
  readonly onRegionChange: (region: CatalogueRegionId) => void;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
}

export function AdminProductsView({ products, region, onRegionChange, isLoading, isError, onRetry }: AdminProductsViewProps) {
  const { t } = useTranslation();
  const state = useAdminProductsViewState(products, isError);
  const { setMissing, search, setSearch } = state;

  // Variant-less products get a placeholder row, which is not a SKU.
  const skuCount = useMemo(() => products.reduce((total, product) => total + (product.variants?.length ?? 0), 0), [products]);
  const columns = useMemo(() => getAdminProductsColumns(t), [t]);
  const coverage = useMemo(() => computeCatalogueCoverage(products), [products]);
  const badgeOptions = useMemo(() => collectBadgeOptions(state.rows), [state.rows]);
  const filterGroups = useMemo(
    () => buildCatalogueFilterGroups({ t, badgeOptions, missing: state.missing, onMissingChange: setMissing }),
    [t, badgeOptions, state.missing, setMissing]
  );

  const handleRegionChange = useCallback(
    (value: string) => {
      if (!isCatalogueRegionId(value)) return;
      onRegionChange(value);
    },
    [onRegionChange]
  );

  const hasQuery = search.trim() !== "";
  const isFiltered = hasQuery || state.missing.length > 0 || state.columnFilters.length > 0;
  const emptyMessage = isFiltered ? t("admin.productCatalogue.states.noResults") : t("admin.productCatalogue.states.empty");
  const isReady = !isLoading && !isError;

  return (
    <div className="space-y-6" data-testid={setAdminProductsViewTestId("container")}>
      <div>
        <h1 className="text-3xl font-bold tracking-tight" data-testid={setAdminProductsViewTestId("title")}>
          {t("admin.productCatalogue.pageTitle")}
        </h1>
        <p className="mt-2 text-muted-foreground">{t("admin.productCatalogue.pageDescription")}</p>
      </div>

      {isReady ? <AdminCatalogueMetrics coverage={coverage} activeMissing={state.missing} onToggleMissing={state.toggleMissing} /> : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select value={region} onValueChange={handleRegionChange}>
          <SelectTrigger
            className="w-full sm:w-56"
            aria-label={t("admin.productCatalogue.region.label")}
            testId={setAdminProductsViewTestId("region-trigger")}
          >
            <SelectValue placeholder={t("admin.productCatalogue.region.placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {CATALOGUE_REGION_IDS.map((id) => (
              <SelectItem key={id} value={id} testId={setAdminProductsViewTestId(`region-${id}`)}>
                {t(`admin.productCatalogue.region.${id}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex flex-1 items-center gap-2">
          <Input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("admin.productCatalogue.search.placeholder")}
            aria-label={t("admin.productCatalogue.search.label")}
            testId={setAdminProductsViewTestId("search")}
          />
          {hasQuery ? (
            <Button variant="outline" onClick={() => setSearch("")} testId={setAdminProductsViewTestId("search-clear")}>
              {t("admin.productCatalogue.search.clear")}
            </Button>
          ) : null}
        </div>
      </div>

      {isError ? (
        <Alert variant="destructive" testId={setAdminProductsViewTestId("error")}>
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>{t("admin.productCatalogue.states.loadError")}</span>
            <Button variant="outline" size="sm" onClick={onRetry} testId={setAdminProductsViewTestId("retry")}>
              {t("admin.productCatalogue.states.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {isReady ? (
        <p className="text-sm text-muted-foreground" data-testid={setAdminProductsViewTestId("summary")}>
          {t("admin.productCatalogue.states.resultSummary", { skuCount, productCount: products.length })}
        </p>
      ) : null}

      <AdminProductEditProvider region={region} rows={state.rows}>
        {/* Keyed by region so an editor open in one region can't submit against another region's rows. */}
        <DataTable
          key={region}
          data={state.tableRows}
          columns={columns}
          searchable={false}
          globalFilter={search}
          onGlobalFilterChange={setSearch}
          globalFilterFn={adminCatalogueGlobalFilterFn}
          filterGroups={filterGroups}
          columnFilters={state.columnFilters}
          onColumnFiltersChange={state.onColumnFiltersChange}
          persistFilters={false}
          sorting={state.sorting}
          onSortingChange={state.onSortingChange}
          pagination={state.pagination}
          onPaginationChange={state.onPaginationChange}
          autoResetPageIndex={false}
          pageSizeOptions={ADMIN_PRODUCTS_PAGE_SIZE_OPTIONS}
          isLoading={isLoading}
          isError={isError}
          errorMessage={t("admin.productCatalogue.states.loadError")}
          emptyMessage={emptyMessage}
          persistenceKey={ADMIN_PRODUCTS_PERSISTENCE_KEY}
          enableColumnVisibility
        />
      </AdminProductEditProvider>
    </div>
  );
}
